import { PureComponent } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, formValueSelector, reduxForm } from "redux-form";

import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import type { Project, ProjectList } from "../../graphql.js";
import type { IGenericCardConfiguration } from "../../redux/modules/dashboard.js";
import { loadProjectList } from "../../redux/modules/project-list.js";
import type { IAppState } from "../../redux/reducer.js";
import { SORT_OPTIONS } from "../../settings/settings.js";
import GenericCardEditingDialog, { type IGenericCardEditingProps } from "../core/GenericCardEditingDialog.js";
import GenericOptionsSelectionAndOrdering, { type ISelectable } from "../core/GenericOptionsSelectionAndOrdering.js";
import RadioButtonSelector, { type IRadioProps, RadioButtonReduxFormField } from "../core/forms/RadioButtonSelector.js";
import { SelectAllNoneWidget } from "../core/forms/SelectWidget.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import type { IProjectsListCardConfig } from "./ProjectsDashboardCard.js";

type IProjectsDashboardCardEditingFormOwnProps = IGenericCardEditingProps;

interface IProjectsDashboardCardEditingFormAppStateProps {
  sorting: string;
  initialValues: Partial<FormData>;
}

interface IProjectsDashboardCardEditingFormDispatchProps {
  loadProjectsList: () => Promise<ProjectList>;
}

export interface IProjectsDashboardCardEditingFormProps
  extends IProjectsDashboardCardEditingFormAppStateProps,
    IProjectsDashboardCardEditingFormDispatchProps,
    IProjectsDashboardCardEditingFormOwnProps,
    InjectedFormProps<FormData, any> {
  // only if this screen is a redux-form
}

function mapStateToProps(
  state: IAppState,
  ownProps: IProjectsDashboardCardEditingFormProps,
): IProjectsDashboardCardEditingFormAppStateProps {
  return {
    sorting: formValueSelector(ProjectsDashboardCardEditingForm.FORM_NAME)(
      state,
      ProjectsDashboardCardEditingForm.SORT_ORDER_FIELD_NAME,
    ),
    initialValues: {
      [GenericCardEditingDialog.CARD_EDITING_DIALOG_CARD_TITLE]: ownProps.initialConfig.title,
      [ProjectsDashboardCardEditingForm.SORT_ORDER_FIELD_NAME]: (
        ownProps.initialConfig.specificConfig as IProjectsListCardConfig
      ).sorting,
    },
  };
}

function mapDispatchToProps(dispatch): IProjectsDashboardCardEditingFormDispatchProps {
  return {
    loadProjectsList: () => dispatch(loadProjectList()),
  };
}

export interface ISelectableProject extends ISelectable, Project {}

interface IProjectsDashboardCardEditingFormState {
  orderedSelectableProjects: ISelectableProject[];
}

class ProjectsDashboardCardEditingForm extends PureComponent<
  IProjectsDashboardCardEditingFormProps,
  IProjectsDashboardCardEditingFormState
> {
  public static FORM_NAME = "PROJECTS_CARD_EDITING_FORM";

  public static SORT_ORDER_FIELD_NAME = "SORT_ORDER";

  constructor(props) {
    super(props);

    this.state = {
      orderedSelectableProjects: [],
    };
  }

  componentDidMount() {
    this.props.loadProjectsList().then((projectList: ProjectList) => {
      const cardConfig: IProjectsListCardConfig = this.props.initialConfig.specificConfig as IProjectsListCardConfig;
      const selectedProjectsConfig: number[] = cardConfig.includeProjects || [];

      // initialize the list of project to be selected and ordered according to the configuration
      let orderedSelectableProjects: ISelectableProject[] = [];
      if (selectedProjectsConfig.length === 0) {
        // all of them are selected
        orderedSelectableProjects = projectList.projects.map((project: Project) => ({ ...project, selected: true }));
      } else {
        // only some of them are selected
        orderedSelectableProjects = projectList.projects.map((project: Project) =>
          selectedProjectsConfig.find((selectedProjectId: number) => selectedProjectId === project.id)
            ? { ...project, selected: true }
            : project,
        );
      }

      orderedSelectableProjects.sort((a: ISelectableProject, b: ISelectableProject) => {
        // sort alphabetically
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });

      if (selectedProjectsConfig.length > 0) {
        orderedSelectableProjects.sort((a: Project, b: Project) => {
          // sort according to the original ordering
          if (selectedProjectsConfig.indexOf(a.id) < selectedProjectsConfig.indexOf(b.id)) {
            return -1;
          }
          if (selectedProjectsConfig.indexOf(a.id) > selectedProjectsConfig.indexOf(b.id)) {
            return 1;
          }
          return 0;
        });
      }

      orderedSelectableProjects.sort((a: ISelectableProject, b: ISelectableProject) => {
        // sort by selection
        if (a.selected && !b.selected) {
          return -1;
        }
        if (!a.selected && b.selected) {
          return 1;
        }
        return 0;
      });

      this.setState({ orderedSelectableProjects: orderedSelectableProjects });
    });
  }

  handleSubmitData = (values) => {
    const cardTitle: string = values[GenericCardEditingDialog.CARD_EDITING_DIALOG_CARD_TITLE];
    const sorting: string = values[ProjectsDashboardCardEditingForm.SORT_ORDER_FIELD_NAME];
    let selectedProjects: ISelectableProject[] = this.state.orderedSelectableProjects.filter(
      (project: ISelectableProject) => project.selected,
    );

    // if all of the projects have been selected we consider none should be hidden (so that new projects appears automatically)
    if (selectedProjects.length === this.state.orderedSelectableProjects.length) {
      selectedProjects = [];
    }

    // if the sort option is custom and no project has been selected then we provide all of them to respect the order
    const projects: ISelectableProject[] =
      sorting === SORT_OPTIONS.CUSTOM.field && selectedProjects.length === 0
        ? this.state.orderedSelectableProjects
        : selectedProjects;

    const configuration: IGenericCardConfiguration = {
      ...this.props.initialConfig,
      title: cardTitle,
      specificConfig: {
        sorting: sorting,
        includeProjects: projects.map((project: ISelectableProject) => project.id),
      },
    };

    this.props.saveCardConfig(configuration); // local saving
    this.props.onClose();
  };

  render() {
    const sortOptions: IRadioProps[] = [SORT_OPTIONS.DATE_MODIFIED, SORT_OPTIONS.NAME, SORT_OPTIONS.CUSTOM].map(
      (sortOption) => ({
        label: sortOption.label,
        name: sortOption.field,
        tooltipText: `Sort projects by ${sortOption.label}`,
      }),
    );

    // TODO create a form field out of the ProjectsSelectionAndOrdering ?
    return (
      <ModalControlledOverlay close={this.props.onClose} isOpen={true}>
        {({ close }) => (
          <GenericCardEditingDialog {...this.props} handleSubmitData={this.handleSubmitData} onClose={close}>
            <div className={"dashboard-card-editing-form-section"}>
              <RadioButtonReduxFormField
                horizontalLayout={false}
                radioButtonsAlignedVertically={true}
                key={`projects-sorting`}
                name={ProjectsDashboardCardEditingForm.SORT_ORDER_FIELD_NAME}
                label={"Sort order"}
                component={RadioButtonSelector}
                options={sortOptions}
              />
            </div>

            <div className={"dashboard-card-editing-form-section"} style={{ minWidth: "350px" }}>
              <div className="form-group">
                <div style={{ position: "relative", top: -10, float: "right" }}>
                  <SelectAllNoneWidget
                    current={-1}
                    max={1}
                    onSelectAll={(all) => (all ? this.selectAllProjects() : this.selectNoProjects())}
                  />
                </div>
                <label className="control-label">{"Activate and order items"}</label>
                <div style={{ padding: "5px" }}>
                  <GenericOptionsSelectionAndOrdering
                    options={this.state.orderedSelectableProjects}
                    disabledSortingTooltip={
                      this.props.sorting !== SORT_OPTIONS.CUSTOM.field &&
                      "To sort this list manually you must select the manually-defined option"
                    }
                    listUpdated={(selectedProjects: Project[]) =>
                      this.setState({ orderedSelectableProjects: selectedProjects })
                    }
                    renderItem={(project: Project) => <ProjectColoredLink project={project} noLink />}
                  />
                </div>
              </div>
            </div>
          </GenericCardEditingDialog>
        )}
      </ModalControlledOverlay>
    );
  }

  selectAllProjects = () => {
    this.setState({
      orderedSelectableProjects: this.state.orderedSelectableProjects.map((project: ISelectableProject) => ({
        ...project,
        selected: true,
      })),
    });
  };

  selectNoProjects = () => {
    this.setState({
      orderedSelectableProjects: this.state.orderedSelectableProjects.map((project: ISelectableProject) => ({
        ...project,
        selected: false,
      })),
    });
  };
  /*
  static validate(...params: Array<any>): FormErrors<any> {
    const values = params[0];  // because the typescript definition is not up to date !
    const props: IProjectsDashboardCardEditingFormProps = params[1];  // because the typescript definition is not up to date !


    return {};
  }
*/
}

export default connect<
  IProjectsDashboardCardEditingFormAppStateProps,
  IProjectsDashboardCardEditingFormDispatchProps,
  IProjectsDashboardCardEditingFormOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: ProjectsDashboardCardEditingForm.FORM_NAME,
    //validate: ProjectsDashboardCardEditingForm.validate
  })(ProjectsDashboardCardEditingForm) as any,
);
